/* eslint-disable react-hooks/set-state-in-effect */
import { useState, useEffect, useCallback } from 'react'
import { kanbify } from '../lib/kanbifyClient'

/* ========== PROCEDURES ========== */
export function useProcedures(companyId) {
    const [procedures, setProcedures] = useState([])
    const [loading, setLoading] = useState(true)

    const fetch = useCallback(async () => {
        if (!companyId) { setProcedures([]); setLoading(false); return }
        const { data } = await kanbify
            .from('procedures')
            .select('*, procedure_steps ( * )')
            .eq('company_id', companyId)
            .order('position')
        // sort steps by position within each procedure
        const sorted = (data || []).map(p => ({
            ...p,
            procedure_steps: (p.procedure_steps || []).sort((a, b) => a.position - b.position)
        }))
        setProcedures(sorted)
        setLoading(false)
    }, [companyId])

    useEffect(() => { fetch() }, [fetch])

    // realtime
    useEffect(() => {
        if (!companyId) return
        const chan = kanbify
            .channel(`procedures:${companyId}`)
            .on('postgres_changes', { event: '*', schema: 'public', table: 'procedures', filter: `company_id=eq.${companyId}` }, () => fetch())
            .on('postgres_changes', { event: '*', schema: 'public', table: 'procedure_steps' }, () => fetch())
            .subscribe()
        return () => { kanbify.removeChannel(chan) }
    }, [companyId, fetch])

    const add = async (title, description) => {
        const { data, error } = await kanbify
            .from('procedures')
            .insert([{ company_id: companyId, title, description, position: procedures.length }])
            .select('*, procedure_steps ( * )')
            .single()
        if (error) return { data: null, error: error.message || 'Nu s-a putut crea procedura.' }
        if (data) setProcedures(p => [...p, { ...data, procedure_steps: [] }])
        return { data, error: null }
    }

    const update = async (id, updates) => {
        const { error } = await kanbify.from('procedures').update(updates).eq('id', id)
        if (error) return { success: false, error: error.message || 'Nu s-a putut actualiza procedura.' }
        setProcedures(p => p.map(proc => proc.id === id ? { ...proc, ...updates } : proc))
        return { success: true, error: null }
    }

    const remove = async (id) => {
        const { error } = await kanbify.from('procedures').delete().eq('id', id)
        if (error) return { success: false, error: error.message || 'Nu s-a putut sterge procedura.' }
        setProcedures(p => p.filter(proc => proc.id !== id))
        return { success: true, error: null }
    }

    const addStep = async (procedureId, text) => {
        const proc = procedures.find(p => p.id === procedureId)
        const position = proc ? (proc.procedure_steps || []).length : 0
        const { data, error } = await kanbify
            .from('procedure_steps')
            .insert([{ procedure_id: procedureId, text, position }])
            .select()
            .single()
        if (error) return { data: null, error: error.message || 'Nu s-a putut adauga pasul.' }
        if (data) {
            setProcedures(p => p.map(proc =>
                proc.id === procedureId
                    ? { ...proc, procedure_steps: [...(proc.procedure_steps || []), data] }
                    : proc
            ))
        }
        return { data, error: null }
    }

    const updateStep = async (stepId, text) => {
        const { error } = await kanbify.from('procedure_steps').update({ text }).eq('id', stepId)
        if (error) return { success: false, error: error.message || 'Nu s-a putut actualiza pasul.' }
        setProcedures(p => p.map(proc => ({
            ...proc,
            procedure_steps: (proc.procedure_steps || []).map(s =>
                s.id === stepId ? { ...s, text } : s
            )
        })))
        return { success: true, error: null }
    }

    const removeStep = async (stepId) => {
        const { error } = await kanbify.from('procedure_steps').delete().eq('id', stepId)
        if (error) return { success: false, error: error.message || 'Nu s-a putut sterge pasul.' }
        setProcedures(p => p.map(proc => ({
            ...proc,
            procedure_steps: (proc.procedure_steps || []).filter(s => s.id !== stepId)
        })))
        return { success: true, error: null }
    }

    const reorderSteps = async (procedureId, orderedStepIds) => {
        if (!procedureId || !Array.isArray(orderedStepIds) || orderedStepIds.length === 0) {
            return { success: false, error: 'Date invalide pentru reordonare.' }
        }

        const updates = orderedStepIds.map((stepId, index) => (
            kanbify
                .from('procedure_steps')
                .update({ position: index })
                .eq('id', stepId)
                .eq('procedure_id', procedureId)
        ))

        const results = await Promise.all(updates)
        const failed = results.find((res) => res.error)
        if (failed?.error) {
            return { success: false, error: failed.error.message || 'Nu s-a putut salva noua ordine a pasilor.' }
        }

        setProcedures((prev) => prev.map((proc) => (
            proc.id === procedureId
                ? {
                    ...proc,
                    procedure_steps: orderedStepIds
                        .map((stepId, index) => {
                            const step = (proc.procedure_steps || []).find((s) => s.id === stepId)
                            return step ? { ...step, position: index } : null
                        })
                        .filter(Boolean),
                }
                : proc
        )))

        return { success: true, error: null }
    }

    return { procedures, loading, fetch, add, update, remove, addStep, updateStep, removeStep, reorderSteps }
}

/* ========== CARD PROCEDURES ========== */
export function useCardProcedures(cardId) {
    const [cardProcedures, setCardProcedures] = useState([])

    const fetch = useCallback(async () => {
        if (!cardId) return
        const { data } = await kanbify
            .from('card_procedures')
            .select('id, card_id, procedure_id, checklist_id, linked_at, linked_by, procedure_frequency, procedure_repeat_interval, procedure_repeat_days, procedure:procedures ( *, procedure_steps ( * ) )')
            .eq('card_id', cardId)
        const sorted = (data || []).map(cp => ({
            ...cp,
            procedure: cp.procedure ? {
                ...cp.procedure,
                procedure_steps: (cp.procedure.procedure_steps || []).sort((a, b) => a.position - b.position)
            } : cp.procedure
        }))
        setCardProcedures(sorted)
    }, [cardId])

    useEffect(() => { fetch() }, [fetch])

    useEffect(() => {
        if (!cardId) return
        let timeout
        const channel = kanbify
            .channel(`card_procedures:${cardId}`)
            .on('postgres_changes', {
                event: '*',
                schema: 'public',
                table: 'card_procedures',
                filter: `card_id=eq.${cardId}`,
            }, () => {
                clearTimeout(timeout)
                timeout = setTimeout(() => fetch(), 250)
            })
            .on('postgres_changes', {
                event: '*',
                schema: 'public',
                table: 'checklists',
                filter: `card_id=eq.${cardId}`,
            }, () => {
                clearTimeout(timeout)
                timeout = setTimeout(() => fetch(), 250)
            })
            .on('postgres_changes', {
                event: '*',
                schema: 'public',
                table: 'procedures',
            }, () => {
                clearTimeout(timeout)
                timeout = setTimeout(() => fetch(), 250)
            })
            .on('postgres_changes', {
                event: '*',
                schema: 'public',
                table: 'procedure_steps',
            }, () => {
                clearTimeout(timeout)
                timeout = setTimeout(() => fetch(), 250)
            })
            .subscribe()

        return () => {
            kanbify.removeChannel(channel)
            clearTimeout(timeout)
        }
    }, [cardId, fetch])

    const link = async (procedureId, linkedBy, scheduleOverride = null) => {
        const { data, error } = await kanbify.rpc('link_procedure_to_card', {
            p_card_id: cardId,
            p_procedure_id: procedureId,
            p_linked_by: linkedBy ?? null,
        })

        if (error) return { success: false, error: error.message || 'Nu s-a putut linkui procedura.' }
        if (data?.success === false) return { success: false, error: data.error || 'Linkuirea a esuat.' }

        if (scheduleOverride && data?.id) {
            const overridePayload = {
                procedure_frequency: scheduleOverride.frequency ?? null,
                procedure_repeat_interval: scheduleOverride.repeat_interval ?? null,
                procedure_repeat_days: scheduleOverride.repeat_days ?? null,
            }
            await kanbify.from('card_procedures').update(overridePayload).eq('id', data.id)
        }

        await fetch()
        return { success: true, data, error: null }
    }

    const updateSchedule = async (cardProcedureId, schedule) => {
        const { data, error } = await kanbify
            .from('card_procedures')
            .update({
                procedure_frequency: schedule.frequency ?? null,
                procedure_repeat_interval: schedule.repeat_interval ?? null,
                procedure_repeat_days: schedule.repeat_days ?? null,
            })
            .eq('id', cardProcedureId)
            .select()
            .single()
        if (error) return { success: false, error: error.message }
        if (!data) return { success: false, error: 'Actualizarea a fost blocata de RLS. Ruleaza politica UPDATE in SQL Editor.' }
        setCardProcedures(prev => prev.map(cp => cp.id === cardProcedureId ? { ...cp, ...data } : cp))
        return { success: true, error: null }
    }

    const unlink = async (id) => {
        const { data: linkRow } = await kanbify
            .from('card_procedures')
            .select('checklist_id')
            .eq('id', id)
            .single()

        const checklistId = linkRow?.checklist_id || null

        const { error } = await kanbify.from('card_procedures').delete().eq('id', id)
        if (error) return { success: false, error: error.message || 'Nu s-a putut dezlega procedura.' }

        if (checklistId) {
            await kanbify.from('checklists').delete().eq('id', checklistId)
        }

        setCardProcedures(p => p.filter(cp => cp.id !== id))
        return { success: true, error: null }
    }

    return { cardProcedures, fetch, link, unlink, updateSchedule }
}
